// src/utils/apolloQueries.js
import { gql, useQuery, useLazyQuery } from '@apollo/client';
import { useEffect } from 'react';
import { consoleDebug } from '../util';

// Fetch policy configurations for different query types
export const FETCH_POLICIES = {
    MENU: {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    },
    PRODUCT: {
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-only',
        notifyOnNetworkStatusChange: true,
    },
    SPECIALS: {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    },
    RETAILER: {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
    },
    CART: {
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-only',
        errorPolicy: 'all',
    }
};

// Custom hook for menu products with Apollo
export const useApolloMenuProducts = (query, variables, options = {}) => {
    const { 
        fetchPolicy = FETCH_POLICIES.MENU,
        onCompleted,
        onError,
        skip = false,
        ...otherOptions 
    } = options;

    const queryResult = useQuery(query, {
        variables,
        skip,
        ...fetchPolicy,
        onCompleted: (data) => {
            consoleDebug('[Apollo Menu Query] Completed:', {
                variables,
                dataReceived: !!data,
                productsCount: data?.menu?.productsCount
            });
            onCompleted?.(data);
        },
        onError: (error) => {
            console.error('[Apollo Menu Query] Error:', error);
            onError?.(error);
        },
        ...otherOptions
    });

    return queryResult;
};

// Custom hook for cart/checkout with optimistic cache updates
export const useApolloCart = (query, variables, options = {}) => {
    const queryResult = useQuery(query, {
        variables,
        ...FETCH_POLICIES.CART,
        ...options
    });

    // Ensure cart data stays fresh
    useEffect(() => {
        const interval = setInterval(() => {
            if (!document.hidden) {
                queryResult.refetch();
            }
        }, 60000); // Refetch every minute when tab is active

        return () => clearInterval(interval);
    }, [queryResult.refetch]);

    return queryResult;
};

// Utility for cache updates after mutations
export const updateCacheAfterMutation = (cache, { query, variables, data, updateFn }) => {
    try {
        const existingData = cache.readQuery({
            query,
            variables
        });

        if (existingData) {
            const updatedData = updateFn(existingData, data);
            
            cache.writeQuery({
                query,
                variables,
                data: updatedData
            });
        }
    } catch (error) {
        console.error('Cache update error:', error);
    }
};

// Helper to invalidate specific queries
export const invalidateQueries = (client, queryNames = []) => {
    queryNames.forEach(queryName => {
        client.cache.evict({
            id: 'ROOT_QUERY',
            fieldName: queryName
        });
    });
    client.cache.gc();
};

// Prefetch utility for critical data
export const prefetchCriticalData = async (client, retailerId) => {
    const queries = [
        {
            query: gql`
                query RetailerQuery($retailerId: ID!) {
                    retailer(id: $retailerId) {
                        id
                        name
                        address
                        hours
                        menuType
                    }
                }
            `,
            variables: { retailerId }
        }
    ];

    await Promise.all(
        queries.map(({ query, variables }) =>
            client.query({
                query,
                variables,
                fetchPolicy: 'network-only'
            })
        )
    );
};
